import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Form, Input, Button, Space, message, Row, Col, Select, Radio } from "antd";
import { ArrowLeftOutlined, SaveOutlined, ReloadOutlined } from "@ant-design/icons";
import userService from "../../services/userService";
import { useCommon } from "../../context/CommonContext";
import { noWhitespaceValidator } from "../../utils/commonFunctions";

const UsersCreateUpdate = () => {
  const { user_id } = useParams();
  const { setPageTitle } = useCommon();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // State management
  const [loading, setLoading] = useState(false);

  const [roles, setRoles] = useState([]);

  const getAllRoles = async () => {
    try {
      const response = await userService.getAllRoles({
        isLimitedContent: true,
      });
      const data = response.data.data.map((role) => ({
        value: role.id,
        label: role.name,
      }));
      setRoles(data);
    } catch (error) {
      console.error("Error getting roles:", error);
    }
  };

  const getUserDetails = async () => {
    try {
      const response = await userService.getUserById(user_id);
      console.log("responseuser==>", response);
      const data = response.data.data;
      form.setFieldsValue(data);
    } catch (error) {
      console.error("Error getting user details:", error);
    }
  };

  useEffect(() => {
    if (user_id !== "new") {
      getUserDetails();
    }
    getAllRoles();
  }, [user_id]);

  useEffect(() => {
    setPageTitle(user_id === "new" ? "Create User" : "Edit User");
  }, []);

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      console.log("Submitting user data:", values);

      if (user_id !== "new") {
        const response = await userService.updateUser(user_id, values);
        console.log("responseupdate==>", response);
      } else {
        const response = await userService.createUser(values);
        console.log("responsecreate==>", response);
      }

      form.resetFields();
      navigate("/cms-users");
    } catch (error) {
      console.error("Error saving user:", error);
      message.error("Failed to save user");
    } finally {
      setLoading(false);
    }
  };

  // Handle form reset
  const handleReset = () => {
    form.resetFields();
  };

  // Handle go back
  const handleGoBack = () => {
    navigate("/cms-users");
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      requiredMark={false}
      initialValues={{
        user_is_active: "Y",
      }}
    >
      <Card title="User Information" style={{ marginBottom: "24px" }}>
        <Row gutter={[16, 16]}>
          {/* Email */}
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="user_email"
              label="Email"
              rules={[
                { required: true, message: "Please enter email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input placeholder="Enter email address" />
            </Form.Item>
          </Col>

          {/* Password */}
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="user_password"
              label="Password"
              rules={[
                {
                  required: user_id === "new",
                  message: "Please enter password",
                },
                {
                  min: 6,
                  message: "Password must be at least 6 characters",
                },
                {
                  pattern:
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/,
                  message:
                    "Password must include uppercase, lowercase, number, and special character",
                },
              ]}
            >
              <Input.Password placeholder="Enter password" />
            </Form.Item>
          </Col>

          {/* Role */}
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="user_role_id"
              label="Role"
              rules={[{ required: true, message: "Please select a role" }]}
            >
              <Select
                placeholder="Select role"
                options={roles}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>

          {/* First Name */}
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="user_firstname"
              label="First Name"
              rules={[
                { required: true, message: "Please enter first name" },
                { validator: noWhitespaceValidator },
              ]}
            >
              <Input placeholder="Enter first name" />
            </Form.Item>
          </Col>

          {/* Last Name */}
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="user_lastname"
              label="Last Name"
              rules={[
                { required: true, message: "Please enter last name" },
                { validator: noWhitespaceValidator },
              ]}
            >
              <Input placeholder="Enter last name" />
            </Form.Item>
          </Col>

          {/* Gender */}
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="user_gender"
              label="Gender"
              rules={[{ required: true, message: "Please select gender" }]}
            >
              <Radio.Group>
                <Radio value="Male">Male</Radio>
                <Radio value="Female">Female</Radio>
                <Radio value="Other">Other</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          {/* Mobile */}
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="user_mobile"
              label="Mobile"
              rules={[
                { required: true, message: "Please enter mobile number" },
                { validator: noWhitespaceValidator },
                {
                  pattern: /^[0-9+\-\s()]+$/,
                  message: "Please enter a valid mobile number",
                },
              ]}
            >
              <Input placeholder="Enter mobile number" />
            </Form.Item>
          </Col>

          {/* City */}
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="user_city"
              label="City"
              rules={[{ required: true, message: "Please enter city" },{validator:noWhitespaceValidator}]}
            >
              <Input placeholder="Enter city" />
            </Form.Item>
          </Col>

          {/* Country */}
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="user_country"
              label="Country"
              rules={[{ required: true, message: "Please enter country" },{validator:noWhitespaceValidator} ]}
            >
              <Input placeholder="Enter country" />
            </Form.Item>
          </Col>

          {/* Active Status */}
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="user_is_active"
              label="Active Status"
              rules={[
                { required: true, message: "Please select active status" },
              ]}
            >
              <Radio.Group>
                <Radio value="Y">Yes</Radio>
                <Radio value="N">No</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* Form Actions */}
      <Form.Item style={{ marginTop: "32px" }}>
        <Space>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={loading}
            size="large"
          >
            {user_id === "new" ? "Create User" : "Update User"}
          </Button>

          <Button icon={<ReloadOutlined />} onClick={handleReset} size="large">
            Reset
          </Button>

          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleGoBack}
            size="large"
          >
            Go Back
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default UsersCreateUpdate;
